import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useInvitation } from '../contexts/InvitationContext';
import { useResponsive } from '../hooks/useResponsive';

interface Props {
  invitedFamilyName: string;
  currentFamilyName?: string;
}

/**
 * FamilyConflict
 *
 * Shown when an existing user (signed in or about to be) is being invited
 * to a different family than the one they're already a member of. APS
 * Recruits enforces one-family-per-account: there is no in-app move, so
 * we block clearly and offer the only two real outs — sign in with a
 * different account, or stay where they are.
 */
const FamilyConflict: React.FC<Props> = ({ invitedFamilyName, currentFamilyName }) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { user, logout } = useAuth();
  const { clearInvitation } = useInvitation();

  const effectiveCurrentFamily = currentFamilyName || user?.family?.name || 'your current family';

  const handleSwitchAccount = async () => {
    try { clearInvitation(); } catch {}
    try { await logout?.(); } catch {}
    navigate('/login');
  };

  const handleStay = () => {
    clearInvitation();
    navigate('/dashboard', { replace: true });
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#fafbff', padding: isMobile ? 16 : 24,
      fontFamily: 'Manrope, sans-serif',
    }}>
      <div style={{
        maxWidth: 560, width: '100%', background: 'white', borderRadius: isMobile ? 12 : 16,
        padding: isMobile ? '32px 24px' : '40px 48px', textAlign: 'center',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: '#FEF3C7', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          marginBottom: 16, fontSize: 24,
        }}>
          ⚠️
        </div>
        <h1 style={{
          fontSize: isMobile ? 22 : 26, fontWeight: 700, color: '#151133',
          margin: 0, marginBottom: 12,
        }}>
          Can't join this family
        </h1>
        <p style={{
          fontSize: 15, color: '#374151', lineHeight: 1.65,
          marginBottom: 8, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto',
        }}>
          This account is already a member of <strong>{effectiveCurrentFamily}</strong>.
        </p>
        <p style={{
          fontSize: 15, color: '#374151', lineHeight: 1.65,
          marginBottom: 24, maxWidth: 460, marginLeft: 'auto', marginRight: 'auto',
        }}>
          APS Recruits accounts can only belong to one family at a time, so this
          account can't accept the invite to <strong>{invitedFamilyName}</strong>.
          If you'd like to join, sign in with a different account.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 360, margin: '0 auto' }}>
          <button
            onClick={handleSwitchAccount}
            style={{
              padding: '14px 20px', background: '#F9550D', color: 'white',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
              cursor: 'pointer', boxShadow: '0 4px 12px rgba(249, 85, 13, 0.25)',
            }}
          >
            Sign in with a different account
          </button>
          <button
            onClick={handleStay}
            style={{
              padding: '14px 20px', background: 'white', color: '#151133',
              border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Stay with {effectiveCurrentFamily}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FamilyConflict;
